import { useMemo } from "react";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { ExpandableSection } from "@/components/ExpandableSection/ExpandableSection";
import { HotelRoomsImageCarousel } from "@/features/hotels/components/hotel/HotelRoomsImageCarousel";
import { RoomFacilityChip } from "@/features/hotels/components/hotel/RoomFacilityChip";
import type { HotelBookingRoomOption } from "@/features/hotels/types/types";

type Props = {
  hotelName: string;
  option: HotelBookingRoomOption;
};

export const RoomDetails = ({ hotelName, option }: Props) => {
  const intl = useIntl();

  const images = useMemo(
    () =>
      option.images?.map(({ url, name }) => ({
        url,
        alt: name,
      })) || [],
    [option.images],
  );

  const description = useMemo(() => {
    const sourceDescription = option.description?.trim();

    if (!sourceDescription) {
      return sourceDescription;
    }

    // Translate room description lines that match the following patterns:
    // Minimum number of adults: 1
    // Maximum number of adults: 4
    // Minimum number of children: 0
    // Maximum number of children: 3
    const regexes = [
      {
        regex: /^Minimum\s+number\s+of\s+adults\s*:\s*(\d+)\s*$/gim,
        replacer: (_: string, count: string) =>
          intl.formatMessage(
            {
              defaultMessage: "Minimum number of adults: {count}",
              description:
                "Room description line showing minimum number of adults",
            },
            { count: Number(count) },
          ),
      },
      {
        regex: /^Maximum\s+number\s+of\s+adults\s*:\s*(\d+)\s*$/gim,
        replacer: (_: string, count: string) =>
          intl.formatMessage(
            {
              defaultMessage: "Maximum number of adults: {count}",
              description:
                "Room description line showing maximum number of adults",
            },
            { count: Number(count) },
          ),
      },
      {
        regex: /^Minimum\s+number\s+of\s+children\s*:\s*(\d+)\s*$/gim,
        replacer: (_: string, count: string) =>
          intl.formatMessage(
            {
              defaultMessage: "Minimum number of children: {count}",
              description:
                "Room description line showing minimum number of children",
            },
            { count: Number(count) },
          ),
      },
      {
        regex: /^Maximum\s+number\s+of\s+children\s*:\s*(\d+)\s*$/gim,
        replacer: (_: string, count: string) =>
          intl.formatMessage(
            {
              defaultMessage: "Maximum number of children: {count}",
              description:
                "Room description line showing maximum number of children",
            },
            { count: Number(count) },
          ),
      },
    ];

    return regexes.reduce(
      (desc, { regex, replacer }) => desc.replace(regex, replacer),
      sourceDescription,
    );
  }, [intl, option.description]);

  const facilities = useMemo(() => {
    return option.facilitiesDescription?.split(",").map((f) => f.trim());
  }, [option.facilitiesDescription]);

  return (
    <div className="space-y-2.5">
      <section className="section-card overflow-clip p-0">
        {images.length ? <HotelRoomsImageCarousel images={images} /> : null}

        <div className="p-4">
          <div className="text-lg font-semibold text-text-primary">
            {option.name}
          </div>

          {hotelName ? (
            <div className="mt-1 text-sm text-text-tertiary">{hotelName}</div>
          ) : null}
        </div>
      </section>

      {description ? (
        <CollapsibleCard
          title={intl.formatMessage({
            defaultMessage: "About Room",
            description: "Section heading for the hotel room description",
          })}
          collapsible={false}
        >
          <ExpandableSection>
            <p className="text-sm whitespace-pre-line text-text-primary">
              {description}
            </p>
          </ExpandableSection>
        </CollapsibleCard>
      ) : null}

      {facilities && facilities.length > 0 ? (
        <CollapsibleCard
          title={intl.formatMessage({
            defaultMessage: "Room Amenities",
            description: "Section heading for the hotel room amenities",
          })}
          collapsible={false}
        >
          <div className="flex flex-wrap gap-2">
            {facilities.map((facility) => (
              <RoomFacilityChip key={facility} label={facility} />
            ))}
          </div>
        </CollapsibleCard>
      ) : null}
    </div>
  );
};
